import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, MessageCircle, ChevronRight, TrendingUp,
  User, DollarSign, Plus, X, AlertCircle, Trash2, ClipboardList,
} from 'lucide-react';
import { auth } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { getClients, createClient, deleteClient } from '../services/client-service';
import { formatPhoneNumber } from '../utils/phoneFormatter';

// ---------------------------------------------------------------------------
// Delete Confirm Modal (module scope)
// ---------------------------------------------------------------------------
const DeleteConfirmModal = ({ clientName, onClose, onConfirm, deleting }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="text-lg font-bold text-gray-900 mb-2">Delete {clientName} and all their data?</h2>
      <p className="text-sm text-gray-600 mb-6">
        This will remove their profile, session notes, and intake form responses. Booking records will be kept for your financial records but marked as archived. This cannot be undone.
      </p>
      <div className="flex gap-3">
        <button
          type="button" onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button" onClick={onConfirm} disabled={deleting}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Add Client Modal (defined at module scope to avoid remount on render)
// ---------------------------------------------------------------------------
const AddClientModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formatted = name === 'phone' ? formatPhoneNumber(value) : value;
    setForm(prev => ({ ...prev, [name]: formatted }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length > 0) { setErrors(v); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to add client. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Add Client</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                name="firstName" value={form.firstName} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.firstName ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Sarah"
              />
              {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                name="lastName" value={form.lastName} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Chen"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              name="email" type="email" value={form.email} onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="sarah@email.com"
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              name="phone" type="tel" value={form.phone} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="(555) 555-5555"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes" value={form.notes} onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Any notes about this client..."
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ClientManagement
// ---------------------------------------------------------------------------
const ClientManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('lastSession');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [practitionerId, setPractitionerId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, displayName }
  const [deleting, setDeleting] = useState(false);

  // Load clients using the onAuthStateChanged + done flag + 3s safety timeout pattern
  useEffect(() => {
    if (!auth) { setLoading(false); return; }

    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        setPractitionerId(user.uid);
        try {
          const data = await getClients(user.uid);
          setClients(data);
        } catch (err) {
          console.error('ClientManagement: error loading clients:', err);
        }
        setLoading(false);
      }
    });

    return () => { done = true; clearTimeout(timeout); unsubscribe(); };
  }, []);

  const toDate = (val) => {
    if (!val) return null;
    if (val.toDate) return val.toDate();
    return new Date(val);
  };

  const filteredClients = useMemo(() => clients
    .filter(client => {
      const matchesSearch =
        client.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'lastSession': {
          const aD = toDate(a.lastSessionDate);
          const bD = toDate(b.lastSessionDate);
          if (!aD && !bD) return 0;
          if (!aD) return 1;
          if (!bD) return -1;
          return bD - aD;
        }
        case 'totalBookings': return (b.totalSessions || 0) - (a.totalSessions || 0);
        case 'totalRevenue':  return (b.totalRevenue || 0) - (a.totalRevenue || 0);
        case 'alphabetical':  return (a.displayName || '').localeCompare(b.displayName || '');
        default: return 0;
      }
    }),
  [clients, searchQuery, filterStatus, sortBy]);

  const getInitials = (name) => (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const formatDate = (val) => {
    const date = toDate(val);
    if (!date) return '—';
    const today = new Date();
    const diffDays = Math.floor((today - date) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalRevenue = clients.reduce((s, c) => s + (c.totalRevenue || 0), 0);
  const activeCount = clients.filter(c => c.status === 'active').length;
  const avgPerClient = clients.length > 0 ? Math.round(totalRevenue / clients.length) : 0;

  const handleAddClient = async (form) => {
    if (!practitionerId) throw new Error('Not authenticated');
    await createClient(practitionerId, form);
    const updated = await getClients(practitionerId);
    setClients(updated);
  };

  const handleDeleteClient = async () => {
    if (!deleteTarget || !practitionerId) return;
    setDeleting(true);
    try {
      await deleteClient(practitionerId, deleteTarget.id);
      const updated = await getClients(practitionerId);
      setClients(updated);
      setDeleteTarget(null);
    } catch (err) {
      console.error('ClientManagement: delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-40" />
            <div className="h-8 bg-gray-200 rounded w-32" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-lg border border-gray-200 h-24" />)}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 h-16" />
            {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 h-20" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <span
            onClick={() => navigate('/dashboard/practitioner')}
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
          >
            Dashboard
          </span>
          <span className="text-gray-400" aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium" aria-current="page">Clients</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
              <p className="text-sm text-gray-600">Manage your client relationships</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard/intake-forms')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <ClipboardList className="w-4 h-4" />
                Manage Intake Forms
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Client
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
                </div>
                <div className="bg-purple-100 rounded-full p-3">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                </div>
                <div className="bg-emerald-100 rounded-full p-3">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${totalRevenue.toLocaleString()}
                  </p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avg. per Client</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {clients.length > 0 ? `$${avgPerClient}` : '—'}
                  </p>
                </div>
                <div className="bg-amber-100 rounded-full p-3">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="" disabled>Sort By</option>
                <option value="alphabetical">Alphabetical</option>
                <option value="lastSession">Last Session</option>
                <option value="totalBookings">Total Bookings</option>
                <option value="totalRevenue">Total Revenue</option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Clients</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Client List */}
        <div className="space-y-3">
          {clients.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No clients yet</h3>
              <p className="text-gray-600 mb-4">
                Clients appear here automatically when they book a session, or you can add them manually.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors"
              >
                Add Your First Client
              </button>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No clients found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          ) : (
            filteredClients.map(client => (
              <div
                key={client.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)', backgroundSize: '400% 400%', animation: 'auroraShift 16s ease infinite' }}
                  >
                    {getInitials(client.displayName)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{client.displayName}</h3>
                      {client.status === 'inactive' && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span>{client.email}</span>
                      <span>•</span>
                      <span>Last session: {formatDate(client.lastSessionDate)}</span>
                      <span>•</span>
                      <span>{client.totalSessions || 0} sessions</span>
                      <span>•</span>
                      <span>${(client.totalRevenue || 0).toLocaleString()} revenue</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/messages?client=${client.id}`)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      aria-label={`Message ${client.displayName}`}
                    >
                      <MessageCircle className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ id: client.id, displayName: client.displayName })}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500"
                      aria-label={`Delete ${client.displayName}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/dashboard/clients/${client.id}`)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors flex items-center gap-1"
                    >
                      View <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <AddClientModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddClient}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          clientName={deleteTarget.displayName}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteClient}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default ClientManagement;
